import { FC, useMemo } from 'react'
import { Trans } from 'react-i18next'

import { FormControl } from '@mui/material'

import { useGetTagListQuery } from '../../../../../api/core'
import { SelectMenu, SelectMenuProps } from '../../../../../components/SelectMenu/SelectMenu'
import { useLiveEventsTrackingUserLanguage } from '../../hooks/useLiveEventsTrackingUserLanguage'

/**
 * Props for the EventTypesSelectMenu component
 */
type EventTypesSelectMenuProps = Omit<SelectMenuProps<string>, 'label' | 'options' | 'onChange'> & {
  /** The currently selected event type ID */
  value: string
  /** Callback function called when the event type selection changes */
  onChange: (eventTypeId: string) => void
}

/**
 * A select menu component for choosing event types in live events tracking.
 *
 * This component fetches available event tags from the API and presents them
 * in a dropdown menu. The options are sorted alphabetically by name and
 * filtered by the user's language preference.
 *
 * @param props - The component props
 * @param props.value - The currently selected event type ID
 * @param props.onChange - Callback function called when selection changes
 * @param props.children - Optional children to render
 * @param props.selectMenuProps - Additional props to pass to the underlying SelectMenu component
 *
 * @returns A FormControl containing a SelectMenu with event type options, or null if data is still loading
 */
export const EventTypesSelectMenu: FC<EventTypesSelectMenuProps> = ({ value, onChange, ...selectMenuProps }) => {
  // Get the user's language preference for live events tracking
  const userLanguage = useLiveEventsTrackingUserLanguage()

  // Fetch event tags from the API, filtered by type 'event' and user language
  const eventTagsQuery = useGetTagListQuery({ type: 'event', userLanguage })

  // Transform API data into select menu options and sort alphabetically
  const eventTypeOptions = useMemo(() => {
    return eventTagsQuery.data?.map((tag) => ({ value: tag.id, name: tag.name })).sort((a, b) => a.name?.localeCompare(b.name)) || []
  }, [eventTagsQuery.data])

  // Only render the select menu when data has finished loading
  return !eventTagsQuery.isFetching ? (
    <FormControl size="small" variant="outlined" fullWidth>
      <SelectMenu
        {...selectMenuProps}
        options={eventTypeOptions}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        label={<Trans i18nKey="internal-live-events:event_type" />}
      />
    </FormControl>
  ) : null
}
